import type {
  AuthInitConfig,
  CrossDomainConfig,
  AuthFlow,
  AuthState,
  AuthStateChangeCallback,
  AuthorizeParams,
  LoginCredentials,
  OAuthTokenResponse,
  AccountsResponse,
  SwitchAccountResponse,
  SessionStatusResponse,
  PopupMessage,
} from "./types.js";
import type { User, AuthTokens } from "@restaurant/shared";
import { createHttpClient, AuthApiError, type HttpClient } from "./internal/httpClient.js";
import { createTokenStore, type TokenStore } from "./internal/tokenStore.js";
import {
  openLoginPopup,
  isPopupBlocked,
  waitForPopupMessage,
} from "./internal/popupManager.js";

type LoginApiResponse = AuthTokens & { user: User };
type RefreshApiResponse = AuthTokens;

/** Proactive refresh cadence. Kept conservative and independent of token
 * expiry (the SDK never decodes JWTs — that's an authentication decision
 * that belongs to the API, per project architecture rules). 401 responses
 * trigger an immediate reactive refresh regardless of this timer. */
const AUTO_REFRESH_INTERVAL_MS = 4 * 60 * 1000;

// ── PKCE helpers (Phase 2a) ───────────────────────────────────────────────

/** sessionStorage key for the in-progress PKCE code verifier. */
const PKCE_SESSION_KEY = "__auth_sdk_pkce__";

async function generatePKCE(): Promise<{ codeVerifier: string; codeChallenge: string }> {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  const codeVerifier = btoa(String.fromCharCode(...array))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");

  const encoder = new TextEncoder();
  const digest = await crypto.subtle.digest("SHA-256", encoder.encode(codeVerifier));
  const codeChallenge = btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");

  return { codeVerifier, codeChallenge };
}

function savePKCE(state: string, codeVerifier: string): void {
  try {
    sessionStorage.setItem(PKCE_SESSION_KEY, JSON.stringify({ state, codeVerifier }));
  } catch {
    // sessionStorage unavailable (e.g. private mode with storage disabled)
  }
}

function consumePKCE(state: string): string | null {
  try {
    const raw = sessionStorage.getItem(PKCE_SESSION_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as { state: string; codeVerifier: string };
    if (stored.state !== state) return null;
    sessionStorage.removeItem(PKCE_SESSION_KEY);
    return stored.codeVerifier;
  } catch {
    return null;
  }
}

class AuthClient {
  private config: CrossDomainConfig | null = null;
  private http: HttpClient | null = null;
  private readonly tokenStore: TokenStore = createTokenStore();
  private state: AuthState = { user: null, isAuthenticated: false, isLoading: false };
  private readonly listeners = new Set<AuthStateChangeCallback>();
  private refreshTimer: ReturnType<typeof setInterval> | null = null;
  private refreshInFlight: Promise<void> | null = null;
  private bootstrapInFlight: Promise<User | null> | null = null;

  /**
   * Initialise the SDK. Accepts the base `AuthInitConfig` (Phase 1) or the
   * extended `CrossDomainConfig` (Phase 2c) for popup + cross-domain support.
   *
   * When `bootstrapOnInit` is true (default in browser), silently restores
   * the session from the API refresh cookie via POST /auth/refresh.
   */
  init(config: AuthInitConfig | CrossDomainConfig): void {
    this.config = config as CrossDomainConfig;
    this.http = createHttpClient({
      baseUrl: config.apiBaseUrl,
      getAccessToken: () => this.tokenStore.get().accessToken,
    });

    const shouldBootstrap =
      config.bootstrapOnInit !== false && typeof window !== "undefined";
    if (shouldBootstrap) {
      this.setState({ isLoading: true });
      void this.bootstrapSession();
    }
  }

  private requireHttp(): HttpClient {
    if (!this.http) {
      throw new Error("Auth SDK not initialized. Call Auth.init(config) before using it.");
    }
    return this.http;
  }

  private setState(patch: Partial<AuthState>): void {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((cb) => cb(this.state));
  }

  private applySession(tokens: AuthTokens, user: User | null): void {
    this.tokenStore.set({ accessToken: tokens.accessToken, refreshToken: tokens.refreshToken });
    this.setState({ user, isAuthenticated: true, isLoading: false });
    this.scheduleAutoRefresh();
  }

  private clearSession(): void {
    this.tokenStore.clear();
    this.stopAutoRefresh();
    this.setState({ user: null, isAuthenticated: false, isLoading: false });
  }

  private scheduleAutoRefresh(): void {
    this.stopAutoRefresh();
    this.refreshTimer = setInterval(() => {
      void this.refresh().catch(() => {
        // Failure already clears the session inside refresh().
      });
    }, AUTO_REFRESH_INTERVAL_MS);
  }

  private stopAutoRefresh(): void {
    if (this.refreshTimer !== null) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /** POST /auth/refresh — uses in-memory refreshToken when present, else HttpOnly cookie. */
  private requestRefreshTokens(): Promise<RefreshApiResponse> {
    const http = this.requireHttp();
    const { refreshToken } = this.tokenStore.get();
    const body = refreshToken ? { refreshToken } : {};
    return http.post<RefreshApiResponse>("/auth/refresh", body);
  }

  /**
   * Silently restore session from POST /auth/refresh (cookie or in-memory token)
   * followed by GET /auth/me. Safe to call multiple times — concurrent calls share
   * one in-flight request. Returns the user when authenticated, otherwise null.
   */
  async bootstrapSession(): Promise<User | null> {
    if (this.bootstrapInFlight) {
      return this.bootstrapInFlight;
    }

    this.setState({ isLoading: true });

    this.bootstrapInFlight = (async () => {
      try {
        const tokens = await this.requestRefreshTokens();
        this.tokenStore.set({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
        });
        const http = this.requireHttp();
        const user = await http.get<User>("/auth/me", { auth: true });
        this.applySession(
          { accessToken: tokens.accessToken, refreshToken: tokens.refreshToken },
          user
        );
        return user;
      } catch {
        this.clearSession();
        return null;
      } finally {
        this.bootstrapInFlight = null;
      }
    })();

    return this.bootstrapInFlight;
  }

  /**
   * Logs in the user.
   *
   * **Embedded login** (credentials provided): calls `POST /auth/login` directly.
   * Intended for the IdP's own login page or first-party forms.
   *
   * **Redirect login** (no credentials, Phase 1 / single-domain): navigates the
   * full page to `config.loginUrl`. After the IdP redirects back, call
   * `bootstrapSession()` (or rely on `bootstrapOnInit` during `init()`) to
   * restore the session from the API refresh cookie.
   *
   * **Popup login** (no credentials, Phase 2c / cross-domain): opens a centered
   * popup window pointing to the IdP login page. On success the IdP posts an
   * `AUTH_SUCCESS` message via `window.postMessage`; the SDK validates the
   * sender origin against `config.idpOrigin`, stores the tokens, and resolves.
   * If the browser blocks the popup the SDK transparently falls back to the
   * full-page redirect flow.
   *
   * Flow selection (when no credentials given):
   *  1. If `config.preferredFlow === "redirect"` → always redirect.
   *  2. If `config.idpOrigin` is set → attempt popup (auto-fallback on block).
   *  3. Otherwise → redirect (legacy Phase 1 behaviour).
   */
  async login(credentials?: LoginCredentials): Promise<User> {
    const http = this.requireHttp();

    // ── Embedded login ──────────────────────────────────────────────────────
    if (credentials) {
      this.setState({ isLoading: true });
      try {
        const data = await http.post<LoginApiResponse>("/auth/login", credentials);
        this.applySession({ accessToken: data.accessToken, refreshToken: data.refreshToken }, data.user);
        return data.user;
      } catch (err) {
        this.setState({ isLoading: false });
        throw err;
      }
    }

    // ── Redirect-only: caller explicitly chose "redirect" ───────────────────
    const { loginUrl, onRedirect, idpOrigin, preferredFlow } = this.config ?? {};
    if (preferredFlow === "redirect" || !idpOrigin) {
      return this._redirectLogin(loginUrl, onRedirect);
    }

    // ── Popup login (Phase 2c) with redirect fallback ───────────────────────
    return this._popupLogin(loginUrl, idpOrigin, onRedirect);
  }

  /** @internal Full-page redirect to the IdP login page. */
  private _redirectLogin(
    loginUrl: string | undefined,
    onRedirect: ((url: string) => void) | undefined
  ): Promise<User> {
    if (!loginUrl) {
      throw new Error("AuthInitConfig.loginUrl is required to use the redirect login flow.");
    }
    const returnUrl = typeof window !== "undefined" ? window.location.href : "";
    const url = `${loginUrl}?returnUrl=${encodeURIComponent(returnUrl)}`;
    if (onRedirect) {
      onRedirect(url);
    } else if (typeof window !== "undefined") {
      window.location.assign(url);
    }
    // Page is navigating away; this promise intentionally never resolves.
    return new Promise<User>(() => {});
  }

  /**
   * @internal Opens a popup to the IdP login page and waits for the
   * `AUTH_SUCCESS` postMessage. Falls back to redirect if the popup is blocked.
   */
  private async _popupLogin(
    loginUrl: string | undefined,
    idpOrigin: string,
    onRedirect: ((url: string) => void) | undefined
  ): Promise<User> {
    if (!loginUrl) {
      throw new Error("AuthInitConfig.loginUrl is required for popup / redirect login.");
    }

    const appOrigin = typeof window !== "undefined" ? window.location.origin : "";
    const popupUrl = `${loginUrl}?popup=1&origin=${encodeURIComponent(appOrigin)}&returnUrl=${encodeURIComponent(appOrigin)}`;

    const popup = openLoginPopup(popupUrl);

    if (isPopupBlocked(popup)) {
      // Fallback: navigate the current page to the IdP.
      return this._redirectLogin(loginUrl, onRedirect);
    }

    this.setState({ isLoading: true });

    let msg: PopupMessage;
    try {
      msg = await waitForPopupMessage(idpOrigin, popup!);
    } catch (err) {
      this.setState({ isLoading: false });
      throw err;
    }

    switch (msg.type) {
      case "AUTH_SUCCESS": {
        // The popup already delivered the access token; we don't get a refresh
        // token via postMessage (the IdP keeps it in its own session / cookie).
        // We store the access token and fetch the full profile.
        this.tokenStore.set({ accessToken: msg.accessToken, refreshToken: null });
        this.setState({ isAuthenticated: true, isLoading: true });
        this.scheduleAutoRefresh();
        try {
          const user = await this.getCurrentUser();
          this.setState({ user, isAuthenticated: true, isLoading: false });
          return user;
        } catch (err) {
          this.clearSession();
          throw err;
        }
      }

      case "AUTH_ERROR":
        this.setState({ isLoading: false });
        throw new AuthApiError(msg.code, msg.message);

      case "AUTH_CANCEL":
        this.setState({ isLoading: false });
        throw new AuthApiError("AUTH_CANCELLED", "Login was cancelled by the user.");
    }
  }

  /**
   * Clears the in-app session.
   *
   * - Default (`localOnly: false`): best-effort `POST /auth/logout` to revoke the
   *   refresh token / shared cookie (global / IdP logout).
   * - `localOnly: true`: only clears tokens and SDK state in this app. Does not
   *   call the API, so the IdP session (e.g. accounts.*) stays signed in.
   */
  async logout(options?: { localOnly?: boolean }): Promise<void> {
    this.setState({ isLoading: true });
    try {
      if (!options?.localOnly) {
        const http = this.requireHttp();
        const { refreshToken } = this.tokenStore.get();
        if (refreshToken) {
          await http.post("/auth/logout", { refreshToken }, { auth: true });
        }
      }
    } catch {
      // Best-effort: always clear the local session even if the API call fails.
    } finally {
      this.clearSession();
    }
  }

  /** Returns the current access token from memory only. Never persisted. */
  getAccessToken(): string | null {
    return this.tokenStore.get().accessToken;
  }

  /** Rotates tokens via POST /auth/refresh. Concurrent calls share one in-flight request. */
  async refresh(): Promise<void> {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    this.refreshInFlight = (async () => {
      try {
        const data = await this.requestRefreshTokens();
        this.tokenStore.set({ accessToken: data.accessToken, refreshToken: data.refreshToken });
        this.scheduleAutoRefresh();
      } catch (err) {
        this.clearSession();
        throw err;
      } finally {
        this.refreshInFlight = null;
      }
    })();

    return this.refreshInFlight;
  }

  /** Calls GET /auth/me. Transparently retries once after a token refresh on 401/expired. */
  async getCurrentUser(): Promise<User> {
    const http = this.requireHttp();
    const fetchMe = () => http.get<User>("/auth/me", { auth: true });

    try {
      const user = await fetchMe();
      this.setState({ user });
      return user;
    } catch (err) {
      const isAuthError =
        err instanceof AuthApiError && (err.code === "UNAUTHORIZED" || err.code === "TOKEN_EXPIRED");
      if (!isAuthError) {
        throw err;
      }
      await this.refresh();
      const user = await fetchMe();
      this.setState({ user });
      return user;
    }
  }

  /**
   * Initiates the OAuth Authorization Code + PKCE flow.
   *
   * Generates a PKCE pair, persists the code_verifier in sessionStorage keyed
   * by `state`, then redirects the browser to the IdP consent page.
   * The caller app must handle the redirect back at `redirectUri` and call
   * `Auth.handleCallback()` to exchange the code for tokens.
   *
   * Requires `AuthInitConfig.consentUrl` to be set.
   */
  async authorize(params: AuthorizeParams): Promise<never> {
    const { consentUrl, onRedirect } = this.config ?? {};
    if (!consentUrl) {
      throw new Error("AuthInitConfig.consentUrl is required to use Auth.authorize().");
    }

    const { codeVerifier, codeChallenge } = await generatePKCE();
    savePKCE(params.state, codeVerifier);

    const query = new URLSearchParams({
      clientId: params.clientId,
      redirectUri: params.redirectUri,
      scope: params.scope,
      state: params.state,
      codeChallenge,
      codeChallengeMethod: "S256",
      responseType: "code",
    });

    const url = `${consentUrl}?${query.toString()}`;

    if (onRedirect) {
      onRedirect(url);
    } else if (typeof window !== "undefined") {
      window.location.assign(url);
    }

    return new Promise<never>(() => {});
  }

  /**
   * Completes the OAuth flow after the IdP redirects back to `redirectUri`.
   *
   * Call this at the callback/redirect page. It reads the PKCE code_verifier
   * from sessionStorage, exchanges the authorization code for tokens via
   * POST /oauth/token, and establishes a session.
   *
   * @param code - The authorization code from the redirect URL's `code` param.
   * @param state - The `state` param from the redirect URL (must match what was sent).
   * @param redirectUri - Must exactly match the `redirectUri` used in `authorize()`.
   * @param clientId - The OAuth client ID used in `authorize()`.
   */
  async handleCallback(params: {
    code: string;
    state: string;
    redirectUri: string;
    clientId: string;
  }): Promise<User> {
    const http = this.requireHttp();
    const codeVerifier = consumePKCE(params.state);
    if (!codeVerifier) {
      throw new AuthApiError(
        "PKCE_VERIFICATION_FAILED",
        "No matching PKCE session found. The state parameter may have been tampered with."
      );
    }

    const data = await http.post<OAuthTokenResponse>("/oauth/token", {
      grantType: "authorization_code",
      code: params.code,
      redirectUri: params.redirectUri,
      codeVerifier,
      clientId: params.clientId,
    });

    this.applySession({ accessToken: data.accessToken, refreshToken: data.refreshToken }, null);

    // Fetch user profile now that we have a valid access token.
    const user = await this.getCurrentUser();
    return user;
  }

  // ── Phase 2b — Multi-Account & SSO ───────────────────────────────────────

  /**
   * Returns all sessions the user has active on this IdP.
   * Maps to GET /auth/accounts. Requires a valid access token.
   */
  async getAccounts(): Promise<AccountsResponse> {
    const http = this.requireHttp();
    return http.get<AccountsResponse>("/auth/accounts", { auth: true });
  }

  /**
   * Switches the active session to another account.
   * Returns new tokens that replace the current session; applies them
   * immediately so subsequent SDK calls use the new access token.
   * Maps to POST /auth/switch-account.
   */
  async switchAccount(userId: string): Promise<User> {
    const http = this.requireHttp();
    const data = await http.post<SwitchAccountResponse>(
      "/auth/switch-account",
      { userId },
      { auth: true }
    );
    this.applySession({ accessToken: data.accessToken, refreshToken: data.refreshToken }, data.user);
    return data.user;
  }

  /**
   * Marks an account as the default so it is pre-selected on the next login.
   * Maps to POST /auth/default-account.
   */
  async setDefaultAccount(userId: string): Promise<void> {
    const http = this.requireHttp();
    await http.post("/auth/default-account", { userId }, { auth: true });
  }

  /**
   * Silent session check — does NOT redirect or throw on unauthenticated.
   * Useful for silent SSO probes from third-party apps.
   * Maps to GET /auth/session/status.
   */
  async checkSession(): Promise<SessionStatusResponse> {
    const http = this.requireHttp();
    try {
      return await http.get<SessionStatusResponse>("/auth/session/status", { auth: true });
    } catch {
      return { isAuthenticated: false };
    }
  }

  // ── Phase 2c — Cross-Domain & Popup Login ─────────────────────────────────

  /**
   * Returns the currently configured preferred auth flow, or the auto-selected
   * default. Consumers can call this to determine which UI to show (e.g. "Open
   * login window" vs. "Sign in" — same button, different labels).
   *
   * Auto-selection rule: "popup" when `idpOrigin` is configured, else "redirect".
   */
  getPreferredFlow(): AuthFlow {
    const { preferredFlow, idpOrigin } = this.config ?? {};
    if (preferredFlow) return preferredFlow;
    return idpOrigin ? "popup" : "redirect";
  }

  onAuthStateChange(callback: AuthStateChangeCallback): () => void {
    this.listeners.add(callback);
    callback(this.state);
    return () => {
      this.listeners.delete(callback);
    };
  }

  getState(): AuthState {
    return { ...this.state };
  }
}

export const Auth = new AuthClient();
